if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;

      this.button = this.querySelector('.load-more');
      this.grid = id ? document.querySelector('[data-id="' + id + '"]') : document.getElementById('product-grid');
      this.nextUrl = this.dataset.nextUrl || '';
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this._columns = 4;
    }

    connectedCallback() {
      if (!this.grid) {
        return;
      }

      const bannerSlots = Array.from(this.grid.querySelectorAll(':scope > li.promotion-column'))
        .reduce((sum, el) => sum + this._bannerWidth(el), 0);
      if (bannerSlots > 0) {
        const productItems = Array.from(this.grid.querySelectorAll(':scope > li:not(.promotion-column)'));
        productItems.slice(-bannerSlots).forEach(el => el.remove());
      }

      const colClass = Array.from(this.grid.classList).find(c => c.startsWith('medium-up-'));
      this._columns = colClass ? (parseInt(colClass.split('-').pop(), 10) || 4) : 4;

      if (this.classList.contains('pagination-type--loadmore')) {
        this.bindLoadMore();
      }

      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }

      this.toggleButtonVisibility();
    }

    bindLoadMore() {
      if (!this.button) {
        return;
      }

      this.button.addEventListener('click', () => {
        this.loadProducts();
        this.button.blur();
      });
    }

    infinite() {
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].intersectionRatio === 1) {
          this.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });

      this.observer.observe(this);
    }

    loadProducts() {
      const url = this.nextUrl;

      if (!url || this.hasAttribute('loading')) {
        return;
      }

      this.setAttribute('loading', true);

      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          this.renderProducts(responseText);

          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('pagination:page-changed', { url: url });
          }
        })
        .catch(() => {
          this.removeAttribute('loading');
        });
    }

    renderProducts(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const gridToAppend = parsed.getElementById('product-grid');

      if (!gridToAppend) {
        this.endPagination();
        return;
      }

      const products = Array.from(gridToAppend.querySelectorAll(':scope > li'))
        .filter(el => !el.classList.contains('promotion-column'));

      if (!products.length) {
        this.endPagination();
        return;
      }

      const fullRows = Math.floor(products.length / this._columns) * this._columns;
      const toAppend = products.slice(0, fullRows || products.length);

      toAppend.forEach((product) => {
        this.grid.appendChild(product);

        if (typeof lazySizes !== 'undefined' && lazySizes.loader) {
          product.querySelectorAll('.lazyload').forEach((image) => {
            lazySizes.loader.unveil(image);
          });
        }
      });

      this.updateNextUrl(parsed);

      if (this.animations_enabled) {
        gsap.set(toAppend, { opacity: 0, y: 30 });
        gsap.to(toAppend, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: () => {
            this.removeAttribute('loading');
          }
        });
      } else {
        this.removeAttribute('loading');
      }

      this.toggleButtonVisibility();
    }

    _bannerWidth(el) {
      const cls = Array.from(el.classList).find(c => c.startsWith('promotion-column-width--'));
      const width = cls ? parseInt(cls.split('--')[1], 10) : 1;
      return isNaN(width) ? 1 : width;
    }

    updateNextUrl(parsedDocument) {
      const sectionId = this.dataset.section;
      let nextPagination = null;

      if (sectionId) {
        nextPagination = parsedDocument.querySelector('pagination-theme[data-section="' + sectionId + '"]');
      }

      if (!nextPagination) {
        nextPagination = parsedDocument.querySelector('pagination-theme');
      }

      this.nextUrl = nextPagination ? (nextPagination.dataset.nextUrl || '') : '';
      this.dataset.nextUrl = this.nextUrl;

      if (!this.nextUrl && this.observer) {
        this.observer.unobserve(this);
      }
    }

    toggleButtonVisibility() {
      if (!this.button) {
        return;
      }

      this.button.classList.toggle('visually-hidden', !this.nextUrl);
    }

    endPagination() {
      this.nextUrl = '';
      this.dataset.nextUrl = '';
      this.removeAttribute('loading');
      this.toggleButtonVisibility();

      if (this.observer) {
        this.observer.unobserve(this);
      }
    }
  }

  customElements.define('pagination-theme', Pagination);
}
